import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import ProductCard from '../components/ProductCard';
import FilterSidebar from '../components/FilterSidebar';
import SEO from '../components/SEO';
import imgHOD from '../assets/products/1000154716.jpg';

const IndianWear = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    priceRange: '',
    size: '',
    sortBy: 'newest'
  });
  const [showFilters, setShowFilters] = useState(false);

  const brandName = 'House of Deepak';

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(cat => cat.brand === brandName)
        .map(cat => cat.name)
        .sort();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // Get all products
      const allProductsQuery = collection(db, 'products');
      const allSnapshot = await getDocs(allProductsQuery);
      const allProducts = allSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter for House of Deepak products client-side
      const houseProducts = allProducts.filter(product => 
        product.brand === 'House of Deepak' && 
        (product.status === 'active' || !product.status)
      );
      
      // Sort client-side
      houseProducts.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || a.createdAt || new Date();
        const bDate = b.createdAt?.toDate?.() || b.createdAt || new Date();
        return bDate - aDate;
      });
      
      setProducts(houseProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Category filter
    if (filters.category) {
      filtered = filtered.filter(product => 
        product.category?.toLowerCase() === filters.category.toLowerCase()
      );
    }

    // Price range filter
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(product => {
        const price = product.discountedPrice || product.price;
        return price >= min && (max ? price <= max : true);
      });
    }

    // Size filter
    if (filters.size) {
      filtered = filtered.filter(product => 
        product.sizes?.includes(filters.size)
      );
    }

    // Sort
    switch (filters.sortBy) {
      case 'price-low':
        filtered.sort((a, b) => (a.discountedPrice || a.price) - (b.discountedPrice || b.price));
        break;
      case 'price-high':
        filtered.sort((a, b) => (b.discountedPrice || b.price) - (a.discountedPrice || a.price));
        break;
      case 'name':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        // Keep original order (newest first)
        break;
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      priceRange: '',
      size: '',
      sortBy: 'newest'
    });
  };

  if (loading) {
    return (
      <>
      <SEO 
        title="Indian Wear Collection"
        description="Explore House of Deepak's exquisite collection of traditional Indian ethnic wear. Shop sarees, lehengas, anarkali suits, salwar kameez, kurtas, and more. Premium quality ethnic fashion for every occasion."
        keywords="Indian ethnic wear, traditional sarees, designer lehengas, anarkali suits, salwar kameez, ethnic kurtas, Indian fashion online, traditional wear, House of Deepak collection"
      />
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-brown mx-auto mb-4"></div>
                <p className="text-warm-gray">Loading products...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Indian Wear Collection - House of Deepak",
    "description": "Explore our exquisite collection of traditional Indian ethnic wear including sarees, lehengas, anarkali suits, and more.",
    "url": "https://houseofdeepak.in/indian-wear",
    "isPartOf": {
      "@type": "WebSite",
      "name": "House of Deepak",
      "url": "https://houseofdeepak.in"
    }
  };

  return (
    <>
    <SEO 
      title="Indian Wear Collection"
      description="Explore House of Deepak's exquisite collection of traditional Indian ethnic wear. Shop sarees, lehengas, anarkali suits, salwar kameez, kurtas, and more. Premium quality ethnic fashion for every occasion."
      keywords="Indian ethnic wear, traditional sarees, designer lehengas, anarkali suits, salwar kameez, ethnic kurtas, Indian fashion online, traditional wear, House of Deepak collection"
      schema={collectionSchema}
    />
    <div className="min-h-screen bg-cream pt-20 pb-16">
      {/* Hero Section */}
      <div className="relative h-[450px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={imgHOD} 
            alt="House of Deepak Collection" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="section-padding w-full">
            <div className="container-max">
              <motion.div
                className="max-w-2xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4 font-light">
                  Indian Ethnic Wear
                </p>
                <h1 className="text-5xl md:text-7xl font-serif font-light text-white mb-6 leading-tight">
                  House of Deepak
                </h1>
                <p className="text-xl text-white/90 max-w-xl mb-8 leading-relaxed">
                  Discover our exquisite collection of traditional Indian wear, where heritage meets contemporary elegance
                </p>
                
                {/* Social Links */}
                <div className="flex flex-wrap items-center gap-6">
                  <a
                    href="https://www.instagram.com/houseofdeepak"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-white/90 hover:text-white transition-colors duration-200 group"
                  >
                    <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors duration-200">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium">@houseofdeepak</span>
                  </a>
                  
                  <a
                    href="mailto:info@houseofdeepak.in"
                    className="flex items-center space-x-2 text-white/90 hover:text-white transition-colors duration-200 group"
                  >
                    <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors duration-200">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium">info@houseofdeepak.in</span>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="section-padding py-16">
        <div className="container-max">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className="lg:w-1/4">
              <div className="lg:hidden mb-6">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center justify-between w-full p-4 bg-white rounded-lg shadow-sm"
                >
                  <span className="font-medium text-deep-brown">Filters</span>
                  <svg
                    className={`w-5 h-5 transform transition-transform duration-200 ${
                      showFilters ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              <AnimatePresence>
                {(showFilters || window.innerWidth >= 1024) && (
                  <FilterSidebar
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={clearFilters}
                    categories={categories}
                    className={showFilters ? 'block' : 'hidden lg:block'}
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Products Grid */}
            <div className="lg:w-3/4">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-serif font-light text-deep-brown">
                  {filteredProducts.length} Products
                </h2>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="px-4 py-2 border border-warm-gray/30 rounded-none bg-white text-deep-brown focus:outline-none focus:border-deep-brown"
                >
                  <option value="newest">Newest First</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name">Name: A to Z</option>
                </select>
              </div>

              {/* Products Grid */}
              {filteredProducts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="mb-6">
                    <svg className="w-16 h-16 mx-auto text-warm-gray/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-serif text-deep-brown mb-2">No products found</h3>
                  <p className="text-warm-gray mb-6">Try adjusting your filters or search criteria.</p>
                  <button
                    onClick={clearFilters}
                    className="btn-secondary"
                  >
                    Clear All Filters
                  </button>
                </div>
              ) : (
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  {filteredProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    >
                      <ProductCard product={product} />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default IndianWear;